import { useState } from "react";
import { Plus, Minus, Phone, ArrowUpRight, HelpCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type FAQ = {
  q: string;
  a: string;
};
const FAQS: FAQ[] = [{
  q: "How much does it cost to replace a single garage door panel in Sydney?",
  a: "Single panel replacement typically costs $480–$890 depending on door size, panel position (bottom panels are easier to access than top), and brand. Standard B&D Panelift bottom panel runs $480–$680. Multi-panel jobs get bulk pricing. Compared to full door replacement ($2,500–$4,500+), single panel swaps save customers 50–60% on average. We always quote fixed-price on-site before any work starts — free if you don't proceed."
}, {
  q: "Can a single damaged panel really be replaced, or do I need a whole new door?",
  a: "In 80% of damage cases — yes, just the panel. Sectional doors are designed for individual panel replacement. We carry 150+ panels in stock at our Bass Hill workshop covering the most common B&D, Steel-Line, Centurion, and Hörmann profiles. We only recommend full door replacement when multiple panels are damaged, the structural frame is compromised, or the door is genuinely beyond repair."
}, {
  q: "How long does a panel replacement take?",
  a: "Most single panel swaps are completed in 60–90 minutes on first visit. Multi-panel jobs take 2–3 hours depending on how many panels. We bring the replacement panel, all tools, and dispose of the damaged panel — you don't need to do anything except open the garage for us."
}, {
  q: "Will you exactly match my existing panel colour and profile?",
  a: "Yes — that's our match guarantee. Every panel is matched for colour (Colorbond shades like white, primrose, paperbark, woodland grey, monument), profile (ribbed, smooth, woodgrain), and embossing. If the match isn't acceptable, we replace it free. For unusual or faded colours, we can factory-order an exact match (5–10 days) or recommend strategic placement to disguise minor variation. Send us a photo first and we'll confirm what's possible."
}, {
  q: "Do you provide quotes for insurance claims?",
  a: "Yes — insurance work is one of our most common job types. We provide itemised written quotes with damage photos, parts breakdown, labour costs, and brand-spec details. Our quotes are formatted to pass assessor review first time — no rework, no rejected claims. For approved claims, we can also bill insurers directly so you don't have to manage the paperwork."
}, {
  q: "My vehicle reversed into the garage door — what should I do first?",
  a: "First: don't operate the door — it could be off the tracks or the structural balance could be off. Take photos of the damage from a few angles, then send them to us on WhatsApp (0485 000 933). We'll identify the brand and panel within minutes, confirm whether we have the panel in stock, and book a same-day or next-day technician. Most vehicle-impact jobs are single panel swaps and don't need a full door replacement."
}, {
  q: "Do you replace panels on roller doors too, or only sectional doors?",
  a: "Both — but the work is different. Sectional doors use individual panels that bolt to hinges. Roller doors use a continuous curtain made of horizontal slats — when a slat is damaged, we can replace individual slats or sections without replacing the entire curtain. For roller doors specifically, see our roller door repair page. For sectional doors, this page covers everything."
}, {
  q: "What warranty do you offer on replacement panels?",
  a: "Every panel replacement includes a 12-month written workmanship warranty, plus the manufacturer's warranty on the panel itself (typically 12–24 months on the panel structure, plus 7–10 year warranty on the Colorbond finish). The warranty card is part of your written invoice. If the panel fails or the door becomes unbalanced inside the warranty period, we come back at no charge."
}];
export default function PanelFAQ() {
  const [open, setOpen] = useState<number | null>(0);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map(faq => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a
      }
    }))
  };
  return <section style={{
    background: NAVY,
    position: "relative",
    overflow: "hidden",
    padding: "120px 24px",
    fontFamily: FONT_BODY
  }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(jsonLd)
    }} />

      <div aria-hidden style={{
      position: "absolute",
      inset: 0,
      background: `radial-gradient(circle at 10% 20%, rgba(245,166,35,0.06) 0%, transparent 50%), radial-gradient(circle at 90% 80%, rgba(245,166,35,0.04) 0%, transparent 50%)`,
      pointerEvents: "none"
    }} />

      <div aria-hidden style={{
      position: "absolute",
      inset: 0,
      opacity: 0.03,
      mixBlendMode: "overlay",
      pointerEvents: "none",
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
    }} />

      <div aria-hidden style={{
      position: "absolute",
      top: 60,
      right: -40,
      fontFamily: FONT_HEAD,
      fontSize: "clamp(160px, 20vw, 280px)",
      fontWeight: 900,
      color: "rgba(255,255,255,0.02)",
      letterSpacing: "-0.04em",
      lineHeight: 1,
      pointerEvents: "none",
      userSelect: "none",
      zIndex: 1
    }}>
        FAQ
      </div>

      <div style={{
      position: "relative",
      zIndex: 2,
      maxWidth: 1200,
      margin: "0 auto"
    }}>
        {/* HEADER */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: 60,
        marginBottom: 64,
        alignItems: "end"
      }} className="panel-faq-header">
          <div>
            <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 20
          }}>
              <span style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD
            }}>
                08 / Panel questions
              </span>
              <span style={{
              width: 32,
              height: 1,
              background: GOLD
            }} />
            </div>

            <h2 style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(34px, 4.5vw, 56px)",
            fontWeight: 800,
            color: "#ffffff",
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
            margin: 0
          }}>
              The panel questions Sydney owners{" "}
              <span style={{
              color: GOLD,
              fontStyle: "italic",
              fontWeight: 700
            }}>
                ask us most.
              </span>
            </h2>
          </div>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: 17,
          color: "rgba(255,255,255,0.7)",
          lineHeight: 1.6,
          margin: 0,
          maxWidth: 460,
          justifySelf: "end"
        }}>
            Don't see your question? Send a photo on WhatsApp — we answer panel
            questions every day, no obligation.
          </p>
        </div>

        {/* ACCORDION + STICKY CTA */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.7fr) minmax(0, 1fr)",
        gap: 48,
        alignItems: "start"
      }} className="panel-faq-body">
          {/* LEFT — Accordion */}
          <div style={{
          display: "flex",
          flexDirection: "column"
        }}>
            {FAQS.map((faq, i) => <FaqRow key={i} faq={faq} index={i} isOpen={open === i} isLast={i === FAQS.length - 1} onToggle={() => setOpen(open === i ? null : i)} />)}
          </div>

          {/* RIGHT — Sticky CTA */}
          <div className="panel-faq-sticky" style={{
          position: "sticky",
          top: 100,
          padding: "40px 32px",
          background: NAVY_DEEP,
          border: `1px solid rgba(245,166,35,0.2)`,
          borderRadius: 4,
          overflow: "hidden"
        }}>
            <div aria-hidden style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: 3,
            height: "100%",
            background: GOLD
          }} />

            <div aria-hidden style={{
            position: "absolute",
            top: "-30%",
            right: "-30%",
            width: 300,
            height: 300,
            background: `radial-gradient(circle, rgba(245,166,35,0.1) 0%, transparent 60%)`,
            pointerEvents: "none"
          }} />

            <div style={{
            position: "relative"
          }}>
              <div style={{
              width: 48,
              height: 48,
              borderRadius: 8,
              background: "rgba(245,166,35,0.15)",
              border: `1px solid rgba(245,166,35,0.3)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 20
            }}>
                <HelpCircle size={24} color={GOLD} strokeWidth={1.8} />
              </div>

              <div style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD,
              marginBottom: 12
            }}>
                Got a dented panel?
              </div>

              <h3 style={{
              fontFamily: FONT_HEAD,
              fontSize: 22,
              fontWeight: 700,
              color: "#ffffff",
              lineHeight: 1.25,
              letterSpacing: "-0.015em",
              margin: "0 0 16px"
            }}>
                Send a photo — save thousands.
              </h3>

              <p style={{
              fontFamily: FONT_BODY,
              fontSize: 14,
              color: "rgba(255,255,255,0.65)",
              lineHeight: 1.55,
              margin: "0 0 28px"
            }}>
                Free advice in 15 minutes. We'll tell you whether a single panel
                swap will fix it instead of a full door replacement.
              </p>

              <div style={{
              display: "flex",
              flexDirection: "column",
              gap: 10
            }}>
                <a href="tel:0485000933" style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                padding: "16px 24px",
                background: GOLD,
                color: NAVY,
                fontFamily: FONT_HEAD,
                fontSize: 15,
                fontWeight: 700,
                borderRadius: 8,
                textDecoration: "none",
                transition: "transform 0.2s, box-shadow 0.2s",
                boxShadow: "0 6px 20px rgba(245,166,35,0.3)"
              }} onMouseEnter={e => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 10px 28px rgba(245,166,35,0.45)";
              }} onMouseLeave={e => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(245,166,35,0.3)";
              }}>
                  <Phone size={16} strokeWidth={2.5} />
                  0485 000 933
                </a>
                <Link to="#contact-us" style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "14px 24px",
                background: "transparent",
                color: "#ffffff",
                fontFamily: FONT_HEAD,
                fontSize: 14,
                fontWeight: 600,
                borderRadius: 8,
                textDecoration: "none",
                border: "1px solid rgba(255,255,255,0.25)",
                transition: "background 0.2s, border-color 0.2s"
              }} onMouseEnter={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = "rgba(255,255,255,0.08)";
                (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.5)";
              }} onMouseLeave={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.25)";
              }}>
                  Free quote form
                  <ArrowUpRight size={14} strokeWidth={2.5} />
                </Link>
              </div>

              <div style={{
              marginTop: 28,
              paddingTop: 24,
              borderTop: "1px solid rgba(255,255,255,0.08)"
            }}>
                <div style={{
                fontFamily: FONT_BODY,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: GOLD_DARK,
                marginBottom: 8
              }}>
                  Operating hours
                </div>
                <div style={{
                fontFamily: FONT_BODY,
                fontSize: 13,
                color: "rgba(255,255,255,0.7)",
                lineHeight: 1.6
              }}>
                  
                  
                  <div style={{
                  color: GOLD,
                  fontWeight: 600,
                  marginTop: 6
                }}>
                    24/7 emergency callouts
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .panel-faq-header {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
            align-items: start !important;
          }
          .panel-faq-body {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
          .panel-faq-sticky {
            position: static !important;
          }
        }
      `}</style>
    </section>;
}
function FaqRow({
  faq,
  index,
  isOpen,
  isLast,
  onToggle
}: {
  faq: FAQ;
  index: number;
  isOpen: boolean;
  isLast: boolean;
  onToggle: () => void;
}) {
  return <div style={{
    borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.08)"
  }}>
      <button onClick={onToggle} aria-expanded={isOpen} style={{
      width: "100%",
      padding: "28px 0",
      background: "transparent",
      border: "none",
      cursor: "pointer",
      textAlign: "left",
      display: "grid",
      gridTemplateColumns: "auto 1fr auto",
      gap: 20,
      alignItems: "center",
      color: "#ffffff",
      fontFamily: FONT_HEAD,
      transition: "color 0.2s"
    }} onMouseEnter={e => {
      (e.currentTarget.children[1] as HTMLElement).style.color = GOLD;
    }} onMouseLeave={e => {
      (e.currentTarget.children[1] as HTMLElement).style.color = isOpen ? GOLD : "#ffffff";
    }}>
        <span style={{
        fontFamily: FONT_HEAD,
        fontSize: 13,
        fontWeight: 800,
        color: isOpen ? GOLD : "rgba(255,255,255,0.35)",
        letterSpacing: "0.08em",
        minWidth: 28,
        transition: "color 0.2s"
      }}>
          {String(index + 1).padStart(2, "0")}
        </span>

        <span style={{
        fontFamily: FONT_HEAD,
        fontSize: "clamp(16px, 1.4vw, 19px)",
        fontWeight: 600,
        color: isOpen ? GOLD : "#ffffff",
        lineHeight: 1.35,
        letterSpacing: "-0.005em",
        transition: "color 0.2s"
      }}>
          {faq.q}
        </span>

        <span style={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        background: isOpen ? GOLD : "rgba(255,255,255,0.05)",
        border: isOpen ? `1px solid ${GOLD}` : "1px solid rgba(255,255,255,0.15)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "background 0.2s, border-color 0.2s"
      }}>
          {isOpen ? <Minus size={16} color={NAVY} strokeWidth={2.5} /> : <Plus size={16} color="#ffffff" strokeWidth={2.5} />}
        </span>
      </button>

      <div style={{
      maxHeight: isOpen ? 500 : 0,
      overflow: "hidden",
      transition: "max-height 0.4s ease, padding 0.3s ease",
      paddingBottom: isOpen ? 28 : 0
    }}>
        <div style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        gap: 20
      }}>
          <div style={{
          minWidth: 28
        }} />
          <p style={{
          fontFamily: FONT_BODY,
          fontSize: 15,
          color: "rgba(255,255,255,0.75)",
          lineHeight: 1.7,
          margin: 0,
          maxWidth: 680
        }}>
            {faq.a}
          </p>
          <div style={{
          width: 36
        }} />
        </div>
      </div>
    </div>;
}